import React from "react";
import PropTypes from "prop-types";
import slugify from "slugify";
import { v4 as uuidv4 } from "uuid";

import Cookies from "../Cookies";
import CookieBannerContent from "./CookieBannerContent";
import { isServer, isUsingCorypha } from "../helpers";
import {
  fetchCoryphaPreferences,
  saveCoryphaPreferences,
  checkVersionCoryphaPreferences,
} from "../coryphaAPI";

const CORYPHA_USER = "rl_corypha_user";
const CONSENT_GIVEN = "rcl_consent_given";
const PREFERENCES_COOKIE = "rcl_preferences_consent";
const STATISTICS_COOKIE = "rcl_statistics_consent";
const MARKETING_COOKIE = "rcl_marketing_consent";

class CookieBanner extends React.Component {
  constructor(props) {
    super(props);

    const {
      preferencesDefaultChecked = false,
      statisticsDefaultChecked = false,
      marketingDefaultChecked = false,
      wholeDomain = false,
    } = this.props;

    this.state = {
      preferencesCookies: preferencesDefaultChecked,
      statisticsCookies: statisticsDefaultChecked,
      marketingCookies: marketingDefaultChecked,
      coryphaPreferences: [],
      consentGiven: undefined, // undefined = not checked yet, true/false = checked
    };

    this.onScroll = this.onScroll.bind(this);
    this.onTogglePreferencesCookies =
      this.onTogglePreferencesCookies.bind(this);
    this.onToggleStatisticsCookies = this.onToggleStatisticsCookies.bind(this);
    this.onToggleMarketingCookies = this.onToggleMarketingCookies.bind(this);
    this.onAcceptAll = this.onAcceptAll.bind(this);
    this.confirm = this.confirm.bind(this);
    this.decline = this.decline.bind(this);
    this.consetsCallback = this.consetsCallback.bind(this);
    this.onToggleCoryphaPreference = this.onToggleCoryphaPreference.bind(this);

    this.cookies = new Cookies(wholeDomain);
  }

  async componentDidMount() {
    const { dismissOnScroll } = this.props;

    // First check if consent has been given
    const consentGiven = await this.cookies.get(CONSENT_GIVEN);

    if (isUsingCorypha(this.props)) {
      if (consentGiven) {
        const { hasNewVersion, preferences } =
          await checkVersionCoryphaPreferences(this.props);

        if (hasNewVersion) {
          await this.cookies.clear();
          console.log("Cleared cookies due to new version");

          this.setState(
            {
              coryphaPreferences: preferences,
              consentGiven: false, // Reset consent if new version
            },
            () => {
              this.forceUpdate();
            }
          );
        } else {
          console.log("Setting consent given to true");
          this.setState({ consentGiven: true });
        }
      } else {
        const preferences = await fetchCoryphaPreferences(this.props);
        console.log("No consent given, fetching preferences");

        this.setState({
          coryphaPreferences: preferences.map((preference) => ({
            ...preference,
            accepted: preference.required,
          })),
          consentGiven: false,
        });
      }
    } else {
      this.setState({ consentGiven: !!consentGiven });
    }

    if (isServer() || dismissOnScroll !== true) {
      return;
    }

    if (window.addEventListener) {
      window.addEventListener("scroll", this.onScroll);
    } else if (window.attachEvent) {
      window.attachEvent("onscroll", this.onScroll); // < IE9
    }
  }

  componentWillUnmount() {
    if (isServer()) {
      return;
    }

    if (window.removeEventListener) {
      window.removeEventListener("scroll", this.onScroll);
    } else if (window.detachEvent) {
      window.detachEvent("onscroll", this.onScroll); // < IE9
    }
  }

  onScroll() {
    this.confirm();
  }

  onTogglePreferencesCookies(value) {
    this.setState({ preferencesCookies: value });
  }

  onToggleStatisticsCookies(value) {
    this.setState({ statisticsCookies: value });
  }

  onToggleMarketingCookies(value) {
    this.setState({ marketingCookies: value });
  }

  onToggleCoryphaPreference(value, preference) {
    const { coryphaPreferences = [] } = this.state;
    const newCoryphaPreferences = [...coryphaPreferences];
    const changedPreference = newCoryphaPreferences.find(
      ({ id }) => id === preference.id
    );

    changedPreference.accepted = value;

    this.setState({ coryphaPreferences: newCoryphaPreferences });
  }

  async onAcceptAll() {
    console.log("onAcceptAll called");
    const {
      onAcceptPreferences = () => {},
      onAcceptStatistics = () => {},
      onAcceptMarketing = () => {},
      onAcceptCoryphaPreferences = () => {},
      coryphaUserId,
    } = this.props;

    const { coryphaPreferences = [] } = this.state;

    if (isUsingCorypha(this.props)) {
      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for Corypha");
      await this.cookies.set({
        key: CORYPHA_USER,
        value: coryphaUserId || uuidv4(),
      });

      for (const { name } of coryphaPreferences) {
        await this.cookies.set(
          `rl_${slugify(name.toLowerCase(), "_")}_consent`
        );
      }

      onAcceptCoryphaPreferences(coryphaPreferences);
      saveCoryphaPreferences(
        this.props,
        coryphaPreferences.map((preference) => ({
          id: preference.id,
          accepted: true,
        }))
      );
    } else {
      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for standard mode");
      await this.cookies.set(PREFERENCES_COOKIE, undefined);
      await this.cookies.set(STATISTICS_COOKIE, undefined);
      await this.cookies.set(MARKETING_COOKIE, undefined);

      onAcceptPreferences();
      onAcceptStatistics();
      onAcceptMarketing();
    }

    this.setState({ consentGiven: true });
  }

  async confirm() {
    console.log("confirm called");
    const { coryphaUserId } = this.props;
    const { coryphaPreferences = [] } = this.state;
    const { onAcceptStatistics = () => {} } = this.props;

    if (isUsingCorypha(this.props)) {
      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for Corypha confirm");
      await this.cookies.set({
        key: CORYPHA_USER,
        value: coryphaUserId || uuidv4(),
      });

      for (const { name, accepted } of coryphaPreferences) {
        await this.cookies.set({
          key: `rl_${slugify(name.toLowerCase(), "_")}_consent`,
          value: accepted,
        });
      }

      saveCoryphaPreferences(
        this.props,
        coryphaPreferences.map((preference) => ({
          id: preference.id,
          accepted: preference.accepted,
        }))
      );
    } else {
      const { preferencesCookies, statisticsCookies, marketingCookies } =
        this.state;

      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for standard confirm");

      if (preferencesCookies) {
        await this.cookies.set(PREFERENCES_COOKIE, undefined);
      } else {
        await this.cookies.remove(PREFERENCES_COOKIE);
      }

      if (statisticsCookies) {
        await this.cookies.set(STATISTICS_COOKIE, undefined);
        onAcceptStatistics();
      } else {
        await this.cookies.remove(STATISTICS_COOKIE);
      }

      if (marketingCookies) {
        await this.cookies.set(MARKETING_COOKIE, undefined);
      } else {
        await this.cookies.remove(MARKETING_COOKIE);
      }
    }

    this.setState({ consentGiven: true });
  }

  async decline() {
    console.log("decline called");
    const {
      onDeclinePreferences = () => {},
      onDeclineStatistics = () => {},
      onDeclineMarketing = () => {},
      onDeclineCoryphaPreferences = () => {},
    } = this.props;

    const { coryphaPreferences = [] } = this.state;

    if (isUsingCorypha(this.props)) {
      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for Corypha decline");
      await this.cookies.remove(CORYPHA_USER);

      for (const { name } of coryphaPreferences) {
        await this.cookies.remove(
          `rl_${slugify(name.toLowerCase(), "_")}_consent`
        );
      }

      onDeclineCoryphaPreferences(coryphaPreferences);
    } else {
      await this.cookies.set(CONSENT_GIVEN, undefined);
      console.log("Set CONSENT_GIVEN cookie for standard decline");
      await this.cookies.remove(PREFERENCES_COOKIE);
      await this.cookies.remove(STATISTICS_COOKIE);
      await this.cookies.remove(MARKETING_COOKIE);

      onDeclinePreferences();
      onDeclineStatistics();
      onDeclineMarketing();
    }

    this.setState({ consentGiven: true });
  }

  async consetsCallback() {
    const {
      onAccept = () => {},
      onAcceptPreferences = () => {},
      onAcceptStatistics = () => {},
      onAcceptMarketing = () => {},
      onDeclinePreferences = () => {},
      onDeclineStatistics = () => {},
      onDeclineMarketing = () => {},
    } = this.props;

    const hasPreferencesCookie = await this.cookies.get(PREFERENCES_COOKIE);
    const hasStatisticsCookie = await this.cookies.get(STATISTICS_COOKIE);
    const hasMarketingCookie = await this.cookies.get(MARKETING_COOKIE);

    onAccept();

    if (hasPreferencesCookie) {
      onAcceptPreferences();
    } else {
      onDeclinePreferences();
    }

    if (hasStatisticsCookie) {
      onAcceptStatistics();
    } else {
      onDeclineStatistics();
    }

    if (hasMarketingCookie) {
      onAcceptMarketing();
    } else {
      onDeclineMarketing();
    }
  }

  render() {
    const {
      styles,
      className,
      message,
      policyLink,
      privacyPolicyLinkText,
      necessaryOptionText,
      preferencesOptionText,
      statisticsOptionText,
      marketingOptionText,
      showDeclineButton,
      acceptButtonText,
      declineButtonText,
      managePreferencesButtonText,
      savePreferencesButtonText,
      showPreferencesOption,
      showStatisticsOption,
      showMarketingOption,
      preferencesDefaultChecked,
      statisticsDefaultChecked,
      marketingDefaultChecked,
    } = this.props;

    const { coryphaPreferences = [], consentGiven } = this.state;

    // If consent status is not yet determined, don't render anything
    if (consentGiven === undefined) {
      return null;
    }

    if (consentGiven) {
      this.consetsCallback();
      return null;
    }

    const contentProps = {
      styles,
      className,
      message,
      policyLink,
      privacyPolicyLinkText,
      necessaryOptionText,
      preferencesOptionText,
      statisticsOptionText,
      marketingOptionText,
      showDeclineButton,
      acceptButtonText,
      declineButtonText,
      managePreferencesButtonText,
      savePreferencesButtonText,
      showPreferencesOption,
      showStatisticsOption,
      showMarketingOption,
      preferencesDefaultChecked,
      statisticsDefaultChecked,
      marketingDefaultChecked,
      onTogglePreferencesCookies: this.onTogglePreferencesCookies,
      onToggleStatisticsCookies: this.onToggleStatisticsCookies,
      onToggleMarketingCookies: this.onToggleMarketingCookies,
      onDecline: this.decline,
      onConfirm: this.confirm,
      onAcceptAll: this.onAcceptAll,
      coryphaPreferences,
      onToggleCoryphaPreference: this.onToggleCoryphaPreference,
    };
    return <CookieBannerContent {...contentProps} />;
  }
}

CookieBanner.protoTypes = {
  className: PropTypes.string,
  styles: PropTypes.object,
  message: PropTypes.string.isRequired,
  wholeDomain: PropTypes.bool,
  policyLink: PropTypes.string,
  privacyPolicyLinkText: PropTypes.string,
  necessaryOptionText: PropTypes.string,
  preferencesOptionText: PropTypes.string,
  statisticsOptionText: PropTypes.string,
  marketingOptionText: PropTypes.string,
  acceptButtonText: PropTypes.string,
  declineButtonText: PropTypes.string,
  managePreferencesButtonText: PropTypes.string,
  savePreferencesButtonText: PropTypes.string,
  showDeclineButton: PropTypes.bool,
  dismissOnScroll: PropTypes.bool,
  showPreferencesOption: PropTypes.bool,
  showStatisticsOption: PropTypes.bool,
  showMarketingOption: PropTypes.bool,
  preferencesDefaultChecked: PropTypes.bool,
  statisticsDefaultChecked: PropTypes.bool,
  marketingDefaultChecked: PropTypes.bool,
  onAccept: PropTypes.func,
  onAcceptPreferences: PropTypes.func,
  onAcceptStatistics: PropTypes.func,
  onAcceptMarketing: PropTypes.func,
  onDeclinePreferences: PropTypes.func,
  onDeclineStatistics: PropTypes.func,
  onDeclineMarketing: PropTypes.func,
  coryphaUserId: PropTypes.string,
  coryphaDocumentCode: PropTypes.string,
  coryphaDocumentLanguage: PropTypes.string,
  coryphaApiKey: PropTypes.string,
  onAcceptCoryphaPreferences: PropTypes.func,
  onDeclineCoryphaPreferences: PropTypes.func,
};

export default CookieBanner;
